import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { getPathLabel, postTrackingEvents } from "./pageTrackingUtils";

const TRACK_ENDPOINT = "/api/v1/advisor_activity/track_auth_advisor";

// Public / unauthenticated routes where the advisor activity tracker has no
// session to authenticate against. Posting tracking events here returns 403
// and just adds noise to error logs.
const AUTH_PATH_PREFIXES = [
  "/login",
  "/signup",
  "/sign-up",
  "/forgot-password",
  "/reset-password",
  "/onboarding",
  "/schedule",
];

function isAuthPath(pathname) {
  if (!pathname) return false;
  return AUTH_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

// Human-readable page names from URL paths
// Order matters — more specific prefixes must come before broader ones

const PAGE_LABELS = [
  // ── CRM top-level pages ──────────────────────────────────
  ["/crm/dashboard", "Dashboard"],
  ["/crm/households/smart-table", "Households Table"],
  ["/crm/users/smart-table", "Users Table"],
  ["/crm/pipeline", "Pipeline"],
  ["/crm/prospects/smart-table", "Prospects Table"],
  ["/crm/onboarding/smart-table", "Onboarding Table"],
  ["/crm/meetings/smart-table", "Meetings Table"],
  ["/crm/support/smart-table", "Support Table"],
  ["/crm/firm-tasks/smart-table", "Firm Tasks Table"],
  ["/crm/youvest-portfolio/smart-table", "YouVest Portfolio Table"],
  ["/crm/forms/smart-table", "Forms Table"],
  ["/crm/notifications/smart-table", "Notifications Table"],
  ["/crm/reminders", "Reminders"],
  ["/crm/agents", "AI Sub Agents"],
  ["/crm/resources", "Advisor Resources"],
  ["/crm/advice-library", "Advice Library Management"],
  ["/crm/documentation", "Documentation"],

  // ── Manager pages ────────────────────────────────────────
  ["/crm/manager/advisor-activity", "Manager: Advisor Activity"],
  ["/crm/manager/parabot-usage", "Manager: ParaBot Usage"],
  ["/crm/manager/dashboard", "Manager Dashboard"],

  // ── Meeting mode ─────────────────────────────────────────
  ["/crm/meeting-mode/", "Meeting Mode"],

  // ── Advisor settings ─────────────────────────────────────
  ["/crm/advisor/settings", "Advisor Settings"],

  // ── Form submissions ─────────────────────────────────────
  ["/crm/forms/", "Form Submission"],

  // ── Content / Learning Lab editing ───────────────────────
  ["/content/learning_lab", "Learning Lab Editor"],

  // ── Planning (standalone) ────────────────────────────────
  ["/planning", "Planning"],
];

// Household sub-page labels — matched against the path AFTER the household UUID
const HOUSEHOLD_PAGES = [
  ["youvest/cover-page", "HH: YouVest Cover Page"],
  ["youvest/activity", "HH: YouVest Activity"],
  ["youvest/tutorials", "HH: YouVest Tutorials"],
  ["youvest", "HH: YouVest"],
  ["dashboard", "HH: Dashboard"],
  ["net-worth", "HH: Net Worth"],
  ["spending/category", "HH: Spending Category"],
  ["spending", "HH: Spending"],
  ["transactions", "HH: Transactions"],
  ["budgets", "HH: Budgets"],
  ["vault", "HH: Vault"],
  ["financial-story", "HH: Financial Story"],
  ["smart-tables", "HH: Smart Tables"],
  ["planning", "HH: Planning"],
  ["learning_lab", "HH: Learning Lab"],
  ["partners", "HH: Partners"],
  ["resources", "HH: Resources"],
  ["settings", "HH: Settings"],
  ["abundo-love-corner", "HH: Abundo Love Corner"],
  ["advisor", "HH: Advisor Dashboard"],
  ["risk-tolerance", "HH: Risk Tolerance"],
  ["values", "HH: Values"],
  ["notes", "HH: Notes"],
  ["emails", "HH: Emails"],
  ["advice-library", "HH: Advice Library"],
];

function getPageLabel(pathname) {
  // Check CRM top-level prefix matches
  const pageLabel = getPathLabel(pathname, PAGE_LABELS);
  if (pageLabel) return pageLabel;

  // Household sub-pages: /crm/households/:uuid/sub-path
  const hhMatch = pathname.match(
    /^\/crm\/households\/[0-9a-f-]{36}\/(.+)/i
  );
  if (hhMatch) {
    const subPath = hhMatch[1];
    return getPathLabel(subPath, HOUSEHOLD_PAGES, "HH: Overview");
  }

  // Bare household page (no sub-path)
  if (pathname.match(/^\/crm\/households\/[0-9a-f-]{36}$/i)) {
    return "HH: Dashboard";
  }

  // Fallback
  if (pathname.startsWith("/crm/")) return "CRM: Other";
  return pathname;
}

// Extract household ID from common URL patterns
function extractHouseholdId(pathname) {
  const match = pathname.match(
    /\/(?:households|youvest)\/([0-9a-f-]{36})/i
  );
  return match ? match[1] : null;
}

/**
 * Tracks advisor page views and time-on-page.
 * Batches events and flushes every 30s or on page unload.
 *
 * Also exports a `trackTab` function that components can call
 * when the user switches tabs within a page.
 */
export function useAdvisorPageTracking() {
  const location = useLocation();
  const { isAdvisor, accessToken } = useAuth();
  // Gate all tracking on an active advisor session. After logout the
  // accessToken is cleared synchronously, which immediately stops the
  // flush timer below from POSTing to /track on the /login screen.
  const trackingActive = isAdvisor && !!accessToken;

  const queueRef = useRef([]);
  const pageEnteredRef = useRef(null);
  const lastPathRef = useRef(null);
  const flushTimerRef = useRef(null);

  // Flush queued events to the backend
  const flush = useCallback(() => {
    if (queueRef.current.length === 0) return;

    const events = [...queueRef.current];
    queueRef.current = [];

    // Fire-and-forget — don't block, retry, or log best-effort telemetry failures.
    postTrackingEvents(TRACK_ENDPOINT, events);
  }, []);

  // Record the end of the current page visit
  const closePage = useCallback(() => {
    if (!lastPathRef.current || !pageEnteredRef.current) return;

    const durationMs = Date.now() - pageEnteredRef.current;
    // Only record if they stayed more than 1 second (filters out redirects)
    if (durationMs < 1000) return;

    queueRef.current.push({
      action_type: "Page View",
      resource_type: "page",
      resource_id: lastPathRef.current,
      details: getPageLabel(lastPathRef.current),
      household_id: extractHouseholdId(lastPathRef.current),
      metadata: {
        path: lastPathRef.current,
        duration_seconds: Math.round(durationMs / 1000),
      },
    });
  }, []);

  // Track tab views — call this from components
  const trackTab = useCallback(
    (tabName, extra = {}) => {
      if (!trackingActive) return;
      if (isAuthPath(lastPathRef.current)) return;

      queueRef.current.push({
        action_type: "Tab View",
        resource_type: "tab",
        resource_id: tabName,
        details: tabName,
        household_id:
          extra.householdId || extractHouseholdId(lastPathRef.current || ""),
        metadata: {
          path: lastPathRef.current,
          tab: tabName,
          ...extra,
        },
      });
    },
    [trackingActive]
  );

  // React to route changes
  useEffect(() => {
    if (!trackingActive) return;

    const newPath = location.pathname;
    // Skip if same path (e.g., query param change)
    if (newPath === lastPathRef.current) return;

    // Close previous page (only if it wasn't an auth path — those weren't tracked)
    if (!isAuthPath(lastPathRef.current)) {
      closePage();
    }

    // Start new page — but don't track auth pages
    if (isAuthPath(newPath)) {
      lastPathRef.current = null;
      pageEnteredRef.current = null;
      return;
    }

    lastPathRef.current = newPath;
    pageEnteredRef.current = Date.now();
  }, [location.pathname, trackingActive, closePage]);

  // Flush timer — every 30 seconds
  useEffect(() => {
    if (!trackingActive) return;

    flushTimerRef.current = setInterval(flush, 30_000);
    return () => clearInterval(flushTimerRef.current);
  }, [trackingActive, flush]);

  // Flush on page unload (tab close, refresh, navigate away)
  useEffect(() => {
    if (!trackingActive) return;

    const handleUnload = () => {
      closePage();

      if (queueRef.current.length === 0) return;

      // sendBeacon can't set Authorization headers, and the server's auth
      // middleware only reads the Authorization header — not query params.
      // Use keepalive fetch so the request carries proper auth on unload.
      postTrackingEvents(TRACK_ENDPOINT, queueRef.current, { keepalive: true });

      queueRef.current = [];
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [trackingActive, closePage]);

  return { trackTab };
}
